import { PersonRole } from '@bp/planung-graphql-types';
import { Table, TableColumns, TeacherIllustration } from '@bp/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useGetTodayForPersonsQuery } from '../../../hooks/useGetTodayForPersonsQuery';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { use_ContractsQuery } from '../../../types/planung-graphql-client-defs';
import { selectSchoolYearContract } from '../../Persons/utils/contracts';
import styles from '../LessonsComponents.module.scss';
import { LessonTableType } from '../TimetableVersion/Table/TimetableVersionLessonsTable';

type LessonTeacherInfoTableType = {
  teacherName: string;
  plannedDeputate: string;
  contractDeputate: string;
  status: string;
};

type TeachersInfoTableProps = {
  lessonsData: LessonTableType[];
  subjectUuid: string;
  subjectName?: string;
  teachers: {
    uuid: string;
    displayNameShort?: string | null;
    displayName?: string | null;
    fullName?: string | null;
    qualifications: { subjectUuid: string }[];
    role?: PersonRole | null;
  }[];
};

export const LessonTeacherInfoTable = ({ lessonsData, subjectUuid, subjectName, teachers }: TeachersInfoTableProps) => {
  const { t } = useTranslation();

  const { sorting, saveSorting } = useColumnsSort('lesson-teacher-info-table');
  const schoolYear = useSelectedSchoolYear();

  const qualifiedTeachers =
    teachers.filter(
      (person) =>
        person.role === PersonRole.Teacher && person.qualifications.some((q) => q.subjectUuid === subjectUuid),
    ) ?? [];

  const [{ data: contractData }] = use_ContractsQuery({
    variables: { personUuids: qualifiedTeachers.map((t) => t.uuid) },
  });

  const data: LessonTeacherInfoTableType[] = useMemo(() => {
    return (
      qualifiedTeachers.map((person) => {
        const plannedDeputate = lessonsData
          .map((lesson) => lesson.teachingLoadInfo?.deputatByTeacher?.get(person.uuid) ?? 0)
          .reduce((acc, curr) => acc + curr, 0);

        // 0 when  no contract
        const currentContract = selectSchoolYearContract(
          contractData?.contracts.filter((c) => c.personUuid === person?.uuid) ?? [],
          schoolYear,
        );
        const contractDeputate = currentContract?.hoursWeekly ?? 0;

        const relation = contractDeputate ? (plannedDeputate / contractDeputate) * 100 : 0;

        return {
          teacherName: person.displayNameShort ?? person.displayName ?? person.fullName ?? '',
          plannedDeputate: plannedDeputate.toFixed(2),
          contractDeputate: contractDeputate?.toFixed(2) ?? '',
          status: relation.toFixed(0) + '%',
        };
      }) ?? []
    );
  }, [contractData?.contracts, lessonsData, qualifiedTeachers, schoolYear]);

  const columns: TableColumns<LessonTeacherInfoTableType>[] = [
    {
      header: `${t('persons.colleagues', { count: 2 })} ${subjectName ? `(${subjectName})` : ''}`,
      id: 'teacherName',
      accessorKey: 'teacherName',
      canExpand: true,
    },
    {
      header: t('common.planned'),
      id: 'plannedDeputate',
      accessorKey: 'plannedDeputate',
      alignment: 'right',
    },
    {
      header: t('contracts.contract'),
      id: 'contractDeputate',
      accessorKey: 'contractDeputate',
      alignment: 'right',
    },

    {
      header: t('common.status'),
      id: 'status',
      accessorKey: 'status',
      alignment: 'right',
    },
  ];

  return (
    <Table<LessonTeacherInfoTableType>
      className={styles['lesson-teacher-info-table']}
      showSort
      sorting={sorting}
      onSortingChange={saveSorting}
      columns={columns}
      data={data}
      canScroll
      emptyStateSettings={{
        size: 'small',
        title: t('persons.noPerson'),
        subtitle: '',
        padding: 'm',
        icon: <TeacherIllustration />,
      }}
      showBorderRadius
      showShadow
      customHeaderHeight='var(--spacing-7)'
      customRowHeight='var(--spacing-7)'
      maxHeight={234}
    />
  );
};
